import React, { useContext, useState } from "react";
import { Link } from "react-router-dom";
import { AuthContext } from "../../../context/AuthContext";
import { updateProfile } from "../../../api/auth";
import {
  User, Truck, Check, Copy, AlertCircle, CheckCircle2, Globe, Store, Camera,
} from "lucide-react";

const GENDERS = [
  { value: "", label: "Non précisé" },
  { value: "male", label: "Homme" },
  { value: "female", label: "Femme" },
  { value: "other", label: "Autre" },
];

const inputCls =
  "w-full px-3 py-2.5 rounded-xl border text-sm bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-indigo-200 dark:focus:ring-indigo-500/30 focus:outline-none focus:border-transparent transition-colors";
const inputOk = "border-slate-200 dark:border-slate-700";
const inputErr = "border-rose-400 dark:border-rose-500/60 focus:ring-rose-200 dark:focus:ring-rose-500/30";

const labelCls = "block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1";

const REQUIRED_MSG = "Ce champ est requis.";
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);
  const handle = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <button onClick={handle} className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300" title="Copy">
      {copied ? <Check size={14} className="text-emerald-500" /> : <Copy size={14} />}
    </button>
  );
}

function SectionCard({ title, subtitle, icon: Icon, children }) {
  return (
    <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm p-5 space-y-4">
      <div className="flex items-center gap-2">
        {Icon && <Icon size={15} className="text-indigo-600 dark:text-indigo-400" />}
        <div>
          <h2 className="text-sm font-semibold text-slate-900 dark:text-slate-100">{title}</h2>
          {subtitle && <p className="text-xs text-slate-500 dark:text-slate-500">{subtitle}</p>}
        </div>
      </div>
      {children}
    </div>
  );
}

function AvatarPicker({ label, previewUrl, onSelect }) {
  return (
    <div className="flex items-center gap-4">
      <div className="relative shrink-0">
        <div className="w-16 h-16 rounded-2xl bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 overflow-hidden flex items-center justify-center">
          {previewUrl ? (
            <img src={previewUrl} alt={label} className="w-full h-full object-cover" />
          ) : (
            <User size={22} className="text-slate-400 dark:text-slate-600" />
          )}
        </div>
        <label className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-indigo-600 text-white flex items-center justify-center cursor-pointer hover:bg-indigo-700 shadow-sm">
          <Camera size={12} />
          <input
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => onSelect(e.target.files?.[0] || null)}
          />
        </label>
      </div>
      <p className="text-xs text-slate-500 dark:text-slate-500">{label}</p>
    </div>
  );
}

// Champ label + input/select (passé en children) + message d'erreur exact,
// requis ou renvoyé par le backend — un seul endroit pour garder ce
// comportement cohérent sur tous les champs du formulaire.
function Field({ label, required, error, className = "", children }) {
  return (
    <div className={className}>
      <label className={labelCls}>
        {label} {required && <span className="text-rose-500">*</span>}
      </label>
      {children}
      {error && (
        <p className="text-xs text-rose-500 dark:text-rose-400 mt-1 flex items-center gap-1">
          <AlertCircle size={11} /> {error}
        </p>
      )}
    </div>
  );
}

function ProfileForm() {
  const { user, refreshUser } = useContext(AuthContext);
  const isChild = !!user?.is_child;
  const store = user?.store || null;

  const [form, setForm] = useState({
    first_name: user?.first_name || "",
    last_name: user?.last_name || "",
    username: user?.username || "",
    email: user?.email || "",
    phone_number: user?.phone_number || "",
    birth_date: user?.birth_date || "",
    gender: user?.gender || "",
    country: user?.country || "",
    city: user?.city || "",
    address: user?.address || "",
    public_profile: !!user?.public_profile,
  });
  const [storeForm, setStoreForm] = useState({
    store_name: store?.store_name || "",
    activity_sector: store?.activity_sector || "",
    store_address: store?.store_address || "",
    website: store?.website || "",
    instagram: store?.instagram || "",
    facebook: store?.facebook || "",
  });

  const [avatarFile, setAvatarFile] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});

  const set = (key, val) => setForm((f) => ({ ...f, [key]: val }));
  const setStoreField = (key, val) => setStoreForm((f) => ({ ...f, [key]: val }));

  const avatarPreview = avatarFile ? URL.createObjectURL(avatarFile) : user?.avatar || null;
  const logoPreview = logoFile ? URL.createObjectURL(logoFile) : store?.logo || null;

  // Validation locale — mêmes contraintes que le backend (email/username/
  // country requis côté User, store_name/activity_sector requis côté
  // Store) pour afficher l'erreur exacte immédiatement, sans aller-retour
  // réseau inutile.
  const validate = () => {
    const errs = {};
    if (!form.email.trim()) errs.email = REQUIRED_MSG;
    else if (!EMAIL_RE.test(form.email.trim())) errs.email = "Adresse email invalide.";
    if (!form.username.trim()) errs.username = REQUIRED_MSG;
    if (!form.country.trim()) errs.country = REQUIRED_MSG;

    if (!isChild) {
      if (!storeForm.store_name.trim()) errs.store_name = REQUIRED_MSG;
      if (!storeForm.activity_sector.trim()) errs.activity_sector = REQUIRED_MSG;
    }
    return errs;
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);

    const localErrors = validate();
    if (Object.keys(localErrors).length > 0) {
      setFieldErrors(localErrors);
      setError("Merci de corriger les champs indiqués ci-dessous.");
      return;
    }
    setFieldErrors({});
    setSaving(true);

    try {
      const hasFile = !!(avatarFile || (logoFile && !isChild));
      let payload;

      if (hasFile) {
        payload = new FormData();
        Object.entries(form).forEach(([k, v]) => payload.append(k, v ?? ""));
        if (!isChild) {
          Object.entries(storeForm).forEach(([k, v]) => payload.append(k, v ?? ""));
        }
        if (avatarFile) payload.append("avatar", avatarFile);
        if (logoFile && !isChild) payload.append("logo", logoFile);
      } else {
        payload = isChild ? { ...form } : { ...form, ...storeForm };
      }

      await updateProfile(payload);
      await refreshUser();
      setAvatarFile(null);
      setLogoFile(null);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      const data = err.response?.data;
      if (data && typeof data === "object" && !Array.isArray(data)) {
        const newErrors = {};
        Object.entries(data).forEach(([k, v]) => {
          newErrors[k] = Array.isArray(v) ? v[0] : v;
        });
        setFieldErrors(newErrors);
        setError(
          data.detail ||
          (Object.keys(newErrors).length
            ? "Merci de corriger les champs indiqués ci-dessous."
            : "Échec de la mise à jour.")
        );
      } else {
        setError("Échec de la mise à jour. Vérifiez votre connexion et réessayez.");
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSave} className="space-y-6 max-w-2xl" noValidate>
      <div>
        <h1 className="text-xl font-semibold text-slate-900 dark:text-slate-100">Mon profil</h1>
        <p className="text-sm text-slate-500 dark:text-slate-500 mt-0.5">
          Modifiez vos informations personnelles{!isChild && " et celles de votre boutique"}.
        </p>
      </div>

      {/* Read-only summary */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="bg-slate-50 dark:bg-slate-800/60 rounded-xl border border-slate-200 dark:border-slate-800 p-3">
          <p className="text-[11px] text-slate-500 dark:text-slate-500 mb-1">Rôle</p>
          <span className="inline-flex items-center px-2 py-0.5 rounded-lg text-xs font-medium bg-indigo-50 dark:bg-indigo-500/10 text-indigo-700 dark:text-indigo-400 border border-indigo-200 dark:border-indigo-500/30">
            {user?.role || "STORE"}
          </span>
        </div>
        <div className="bg-slate-50 dark:bg-slate-800/60 rounded-xl border border-slate-200 dark:border-slate-800 p-3">
          <p className="text-[11px] text-slate-500 dark:text-slate-500 mb-1">Statut</p>
          <span className={`inline-flex items-center px-2 py-0.5 rounded-lg text-xs font-medium border ${
            user?.status === "ACTIVE"
              ? "bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-200 dark:border-emerald-500/30"
              : "bg-rose-50 dark:bg-rose-500/10 text-rose-700 dark:text-rose-400 border-rose-200 dark:border-rose-500/30"
          }`}>
            {user?.status || "—"}
          </span>
        </div>
        <div className="bg-slate-50 dark:bg-slate-800/60 rounded-xl border border-slate-200 dark:border-slate-800 p-3">
          <p className="text-[11px] text-slate-500 dark:text-slate-500 mb-1">Type de compte</p>
          <p className="text-sm text-slate-700 dark:text-slate-300">{isChild ? "Compte enfant" : "Compte principal"}</p>
        </div>
        <div className="bg-slate-50 dark:bg-slate-800/60 rounded-xl border border-slate-200 dark:border-slate-800 p-3">
          <div className="flex items-center gap-2">
            <p className="text-[11px] text-slate-500 dark:text-slate-500 flex-1">Store ID</p>
            {store?.id && <CopyButton text={store.id} />}
          </div>
          <p className="font-mono text-xs text-slate-700 dark:text-slate-300 truncate">{store?.id || "—"}</p>
        </div>
      </div>

      {isChild && (
        <div className="flex items-start gap-2.5 p-3.5 bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/30 rounded-xl text-sm text-amber-700 dark:text-amber-400">
          <AlertCircle size={16} className="shrink-0 mt-0.5" />
          En tant que compte enfant, vous ne pouvez modifier que vos informations personnelles (pas celles de la boutique).
        </div>
      )}

      <SectionCard title="Photo de profil" icon={User}>
        <AvatarPicker label="JPG, PNG. 2 Mo max." previewUrl={avatarPreview} onSelect={setAvatarFile} />
      </SectionCard>

      <SectionCard title="Identité" icon={User}>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Prénom" error={fieldErrors.first_name}>
            <input value={form.first_name} onChange={(e) => set("first_name", e.target.value)} className={`${inputCls} ${fieldErrors.first_name ? inputErr : inputOk}`} />
          </Field>
          <Field label="Nom" error={fieldErrors.last_name}>
            <input value={form.last_name} onChange={(e) => set("last_name", e.target.value)} className={`${inputCls} ${fieldErrors.last_name ? inputErr : inputOk}`} />
          </Field>
          <Field label="Nom d'utilisateur" required error={fieldErrors.username}>
            <input value={form.username} onChange={(e) => set("username", e.target.value)} className={`${inputCls} ${fieldErrors.username ? inputErr : inputOk}`} />
          </Field>
          <Field label="Email" required error={fieldErrors.email}>
            <input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className={`${inputCls} ${fieldErrors.email ? inputErr : inputOk}`} />
          </Field>
          <Field label="Téléphone" error={fieldErrors.phone_number}>
            <input value={form.phone_number} onChange={(e) => set("phone_number", e.target.value)} placeholder="+212600000000" className={`${inputCls} ${fieldErrors.phone_number ? inputErr : inputOk}`} />
          </Field>
          <Field label="Date de naissance" error={fieldErrors.birth_date}>
            <input type="date" value={form.birth_date || ""} onChange={(e) => set("birth_date", e.target.value)} className={`${inputCls} ${fieldErrors.birth_date ? inputErr : inputOk}`} />
          </Field>
          <Field label="Genre" error={fieldErrors.gender}>
            <select value={form.gender} onChange={(e) => set("gender", e.target.value)} className={`${inputCls} ${fieldErrors.gender ? inputErr : inputOk}`}>
              {GENDERS.map((g) => <option key={g.value} value={g.value}>{g.label}</option>)}
            </select>
          </Field>
        </div>
      </SectionCard>

      <SectionCard title="Localisation" icon={Globe}>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Pays" required error={fieldErrors.country}>
            <input value={form.country} onChange={(e) => set("country", e.target.value)} className={`${inputCls} ${fieldErrors.country ? inputErr : inputOk}`} />
          </Field>
          <Field label="Ville" error={fieldErrors.city}>
            <input value={form.city} onChange={(e) => set("city", e.target.value)} className={`${inputCls} ${fieldErrors.city ? inputErr : inputOk}`} />
          </Field>
          <Field label="Adresse" error={fieldErrors.address} className="col-span-2">
            <input value={form.address} onChange={(e) => set("address", e.target.value)} className={`${inputCls} ${fieldErrors.address ? inputErr : inputOk}`} />
          </Field>
        </div>
      </SectionCard>

      <SectionCard title="Confidentialité">
        <label className="flex items-center gap-2.5 cursor-pointer">
          <input
            type="checkbox"
            checked={form.public_profile}
            onChange={(e) => set("public_profile", e.target.checked)}
            className="w-4 h-4 rounded border-slate-300 dark:border-slate-600 text-indigo-600 focus:ring-indigo-500"
          />
          <span className="text-sm text-slate-700 dark:text-slate-300">Rendre mon profil visible publiquement</span>
        </label>
      </SectionCard>

      {!isChild && (
        <SectionCard title="Ma boutique" subtitle="Visible par vos partenaires et sur le marketplace" icon={Store}>
          <AvatarPicker label="Logo de la boutique — JPG, PNG. 2 Mo max." previewUrl={logoPreview} onSelect={setLogoFile} />

          <div className="grid grid-cols-2 gap-4">
            <Field label="Nom de la boutique" required error={fieldErrors.store_name}>
              <input value={storeForm.store_name} onChange={(e) => setStoreField("store_name", e.target.value)} className={`${inputCls} ${fieldErrors.store_name ? inputErr : inputOk}`} />
            </Field>
            <Field label="Secteur d'activité" required error={fieldErrors.activity_sector}>
              <input value={storeForm.activity_sector} onChange={(e) => setStoreField("activity_sector", e.target.value)} className={`${inputCls} ${fieldErrors.activity_sector ? inputErr : inputOk}`} />
            </Field>
            <Field label="Plan d'abonnement">
              <input value={store?.subscription_plan || "—"} disabled className={`${inputCls} ${inputOk} opacity-60 cursor-not-allowed`} />
            </Field>
            <Field label="Site web" error={fieldErrors.website}>
              <input value={storeForm.website} onChange={(e) => setStoreField("website", e.target.value)} placeholder="https://" className={`${inputCls} ${fieldErrors.website ? inputErr : inputOk}`} />
            </Field>
            <Field label="Instagram" error={fieldErrors.instagram}>
              <input value={storeForm.instagram} onChange={(e) => setStoreField("instagram", e.target.value)} className={`${inputCls} ${fieldErrors.instagram ? inputErr : inputOk}`} />
            </Field>
            <Field label="Facebook" error={fieldErrors.facebook}>
              <input value={storeForm.facebook} onChange={(e) => setStoreField("facebook", e.target.value)} className={`${inputCls} ${fieldErrors.facebook ? inputErr : inputOk}`} />
            </Field>
            <Field label="Adresse de la boutique" error={fieldErrors.store_address} className="col-span-2">
              <input value={storeForm.store_address} onChange={(e) => setStoreField("store_address", e.target.value)} className={`${inputCls} ${fieldErrors.store_address ? inputErr : inputOk}`} />
            </Field>
          </div>
        </SectionCard>
      )}

      {/* Delivery partners — pas d'infos user/store, mais pas d'autre home logique */}
      <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-4 shadow-sm flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-xl bg-slate-100 dark:bg-slate-800">
            <Truck size={16} className="text-slate-600 dark:text-slate-400" />
          </div>
          <div>
            <p className="font-medium text-slate-900 dark:text-slate-100 text-sm">Partenaires Livraison</p>
            <p className="text-xs text-slate-500 dark:text-slate-500">Gérez vos prestataires de livraison.</p>
          </div>
        </div>
        <Link
          to="/settings/delivery-partners"
          className="shrink-0 inline-flex items-center gap-2 px-3 py-2 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 rounded-xl text-sm font-medium hover:bg-slate-200 dark:hover:bg-slate-700"
        >
          Gérer
        </Link>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-3 rounded-xl bg-rose-50 dark:bg-rose-500/10 border border-rose-200 dark:border-rose-500/30 text-rose-700 dark:text-rose-400 text-sm">
          <AlertCircle size={15} className="shrink-0" />
          {error}
        </div>
      )}
      {success && (
        <div className="flex items-center gap-2 p-3 rounded-xl bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-500/30 text-emerald-700 dark:text-emerald-400 text-sm">
          <CheckCircle2 size={15} className="shrink-0" />
          Profil mis à jour avec succès.
        </div>
      )}

      <button
        type="submit"
        disabled={saving}
        className="px-5 py-2.5 bg-indigo-600 text-white rounded-xl text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {saving ? "Sauvegarde..." : "Sauvegarder"}
      </button>
    </form>
  );
}

export default function SettingsPage() {
  return (
    <div className="space-y-6">
      <ProfileForm />
    </div>
  );
}
